import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Clock, ArrowRight, Loader2, BookOpen } from 'lucide-react';
import { BlogLayout } from '../components/BlogLayout';
import { BlogSeo } from '../components/BlogSeo';

const API = '/api';

export const BlogIndexPage = () => {
  const { cat } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeLabel = categories.find((c) => c.slug === cat)?.label;

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = cat ? `?category=${encodeURIComponent(cat)}` : '';
    Promise.all([
      axios.get(`${API}/blog/articles${params}`),
      axios.get(`${API}/blog/categories`),
    ])
      .then(([a, c]) => {
        if (!active) return;
        setArticles(a.data.articles || []);
        setCategories(c.data.categories || []);
      })
      .catch(() => active && setArticles([]))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [cat]);

  const path = cat ? `/blog/categorie/${cat}` : '/blog';
  const title = activeLabel
    ? `${activeLabel} — Fiches documentaires médicales | Hippocrate MG`
    : 'Blog documentaire médical — Pathologies & symptômes | Hippocrate MG';
  const description = activeLabel
    ? `Fiches documentaires de synthèse en ${activeLabel} pour professionnels de santé : signes cliniques, démarche diagnostique et prise en charge, avec sources officielles françaises.`
    : "Fiches documentaires de synthèse sur les pathologies et symptômes les plus recherchés : diagnostic, prise en charge et sources officielles (HAS, Ameli, Collèges). Pour professionnels de santé.";

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog documentaire Hippocrate MG',
    url: `https://hippocrate.mg${path}`,
    inLanguage: 'fr',
    publisher: { '@type': 'Organization', name: 'Hippocrate MG', url: 'https://hippocrate.mg/' },
  };

  return (
    <BlogLayout>
      <BlogSeo title={title} description={description} path={path} image="https://hippocrate.mg/og-image.jpg" jsonLd={jsonLd} />

      {/* Hero */}
      <section className="border-b border-slate-800/70 bg-gradient-to-b from-slate-900/60 to-slate-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-3 py-1 text-xs text-indigo-300 mb-4">
            <BookOpen className="w-3.5 h-3.5" /> Fiches documentaires
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl"
            style={{ fontFamily: 'Outfit, Manrope, system-ui, sans-serif' }}
            data-testid="blog-index-title"
          >
            {activeLabel ? activeLabel : 'Médecine fondée sur les preuves,'}
            {!activeLabel && <span className="text-indigo-400"> en synthèse.</span>}
          </h1>
          <p className="mt-4 text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl">
            {activeLabel
              ? `Nos fiches documentaires en ${activeLabel.toLowerCase()}, sourcées et pensées pour la pratique.`
              : "Des fiches de synthèse sur les pathologies les plus recherchées — diagnostic, prise en charge et sources officielles françaises. Rédigées pour les professionnels de santé."}
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Filtres catégories */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-category-filters">
            <button
              onClick={() => navigate('/blog')}
              data-testid="blog-cat-all"
              className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors min-h-[36px] ${
                !cat ? 'bg-indigo-600 border-indigo-500 text-white' : 'border-slate-700 text-slate-300 hover:border-indigo-500/60'
              }`}
            >
              Tous
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => navigate(`/blog/categorie/${c.slug}`)}
                data-testid={`blog-cat-${c.slug}`}
                className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors min-h-[36px] ${
                  cat === c.slug ? 'bg-indigo-600 border-indigo-500 text-white' : 'border-slate-700 text-slate-300 hover:border-indigo-500/60'
                }`}
              >
                {c.label} <span className="text-slate-500">· {c.count}</span>
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="blog-empty">
            <BookOpen className="w-10 h-10 mx-auto mb-3 text-slate-600" />
            <p>Aucune fiche publiée pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="blog-article-grid">
            {articles.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                data-testid={`blog-card-${a.slug}`}
                className="group flex flex-col rounded-2xl border border-slate-800 bg-slate-900/50 hover:border-indigo-500/60 hover:bg-slate-900 transition-colors p-5"
              >
                <div className="flex items-center gap-2 text-xs mb-3">
                  <span className="rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 px-2.5 py-0.5">
                    {a.category_label}
                  </span>
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Clock className="w-3 h-3" /> {a.reading_minutes} min
                  </span>
                </div>
                <h2
                  className="text-lg font-semibold text-white leading-snug group-hover:text-indigo-300 transition-colors"
                  style={{ fontFamily: 'Outfit, Manrope, system-ui, sans-serif' }}
                >
                  {a.title}
                </h2>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-3">{a.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-indigo-400 font-medium">
                  Lire la fiche <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </BlogLayout>
  );
};

export default BlogIndexPage;
